import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import styled, { useTheme } from "styled-components";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  FiAlertTriangle, FiCheckCircle, FiFileText, FiLayers, FiPlay, FiPlus, FiRefreshCw, FiSearch,
  FiTrash2, FiX, FiCalendar, FiArrowRight,
} from "react-icons/fi";
import {
  LiveDot, Pill, Group, Empty, Section, SectionHeader, SearchField, Skeleton, Widget, WidgetGrid,
  IconTile, Meter, tokens,
} from "../../activity-logs/styles";
import { SegmentedControl, Sparkline } from "../../activity-logs/components/primitives";
import { IOS } from "../../activity-logs/labels";
import { CreditsPage, DirectoryCard, SupplierGrid, Tag, Notice, PeriodStats } from "../../creditos/styles";
import { ScrolledTopBar, UpdatedAgo } from "../../creditos/components/parts";
import {
  Capsule, PrimaryCapsule, RoundButton, TintedCapsule, TintedDanger, Toolbar, ToolbarDivider,
} from "../../creditos/components/ControlBar";
import { SANS } from "../../creditos/styles";
import { tokens as T, hairline, surface } from "../../../styles/designTokens";
import CoverageMap from "./CoverageMap";
import { buildScope } from "./analiseData";
import {
  branchLabel, buildRevisorHome, conformityColor, formatCount, formatMonthKey,
  formatPercent, periodLabel,
} from "./revisorData";

/**
 * Tela inicial do Revisor SPED: um dashboard das análises do projeto que
 * continua sendo o lugar de criar e abrir análises.
 * Pergunta da tela: quanto dos documentos dos SPEDs tem XML, onde estão as
 * falhas e qual período falta analisar?
 * Elemento próprio: o mapa de cobertura (estabelecimento × mês), em que um mês
 * sem análise abre a criação já com aquele período.
 */

const STEP = 24;

// ─── Gráfico de conformidade por período ─────────────────────────────────────

function DocsTooltip({ active, payload, label }) {
  const theme = useTheme();
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div style={{
      background: tokens.solid(theme),
      border: `1px solid ${tokens.separator(theme)}`,
      borderRadius: 10,
      padding: "10px 12px",
      boxShadow: tokens.dk(theme) ? "0 8px 24px rgba(0,0,0,0.45)" : "0 8px 24px rgba(15,23,42,0.08)",
      fontSize: 12,
      fontVariantNumeric: "tabular-nums",
      color: tokens.label(theme),
      minWidth: 170,
    }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
      {[...payload].reverse().map((p) => (
        <div key={p.dataKey} style={{ display: "flex", justifyContent: "space-between", gap: 14, marginTop: 2 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
            {p.name}
          </span>
          <span>{formatCount(p.value)}</span>
        </div>
      ))}
      <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${tokens.separator(theme)}`, color: tokens.label2(theme) }}>
        {formatPercent(row.pct)} com XML
      </div>
    </div>
  );
}

function ConformityChart({ chrono }) {
  const theme = useTheme();
  const data = chrono.map((r) => ({
    label: r.startKey === r.endKey ? formatMonthKey(r.startKey) : `${formatMonthKey(r.startKey)}+`,
    encontradas: r.encontradas,
    faltantes: r.faltantes,
    pct: r.pct,
  }));
  const axis = { tick: { fontSize: 10, fill: tokens.label2(theme) }, axisLine: false, tickLine: false };
  return (
    <ResponsiveContainer width="100%" height={230}>
      <BarChart data={data} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke={tokens.separator(theme)} />
        <XAxis dataKey="label" {...axis} />
        <YAxis width={56} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)} mil` : v)} {...axis} />
        <Tooltip
          cursor={{ fill: surface(theme, 1) }}
          content={<DocsTooltip />}
        />
        <Bar dataKey="encontradas" name="Encontrados" stackId="x" fill={IOS.green} fillOpacity={0.8} maxBarSize={32} />
        <Bar dataKey="faltantes" name="Faltantes" stackId="x" fill={IOS.red} fillOpacity={0.8} maxBarSize={32} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Cards das análises ──────────────────────────────────────────────────────

export const NewCard = styled(DirectoryCard)`
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 10px;
  min-height: 196px;
  border-style: dashed;
  & > .head, & > .body { opacity: 1; }
  .plus {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: #fff;
    background: ${T.brand};
    transition: transform 160ms cubic-bezier(0.2, 0.9, 0.3, 1.2);
  }
  &:hover:not(:disabled) .plus { transform: scale(1.06); }
  &:active:not(:disabled) .plus { transform: scale(0.94); }
  .t { font-weight: 600; font-size: 0.92em; }
  .s { font-size: 0.76em; color: ${({ theme }) => tokens.label2(theme)}; }
`;

const SPED_MISSING = "#ff453a";

// Um traço por SPED esperado: verde quando enviado, vermelho quando falta
const ScopeStrip = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 0.5px solid ${({ theme }) => hairline(theme, true)};

  .pills { flex: 1; min-width: 0; display: flex; gap: 2px; }
  .pills i { flex: 1; min-width: 0; height: 4px; border-radius: 2px; background: ${T.brand}; }
  .pills i.off { background: ${SPED_MISSING}; }
  .lbl { flex-shrink: 0; font-size: 0.74em; white-space: nowrap; font-variant-numeric: tabular-nums; color: ${({ theme }) => tokens.label2(theme)}; }
  .lbl.warn { font-weight: 600; color: ${SPED_MISSING}; }
`;

function ReportCard({ report, allReports, delay, deleting, onOpen, onDelete }) {
  const color = conformityColor(report.pct);
  const scope = useMemo(() => buildScope(report, allReports), [report, allReports]);
  const absent = scope.absent.map((e) => branchLabel(e.cnpj));
  const scopeTitle = scope.complete
    ? `Todos os ${formatCount(scope.expected)} SPEDs esperados foram enviados`
    : `Sem SPED: ${absent.join(", ")}. Abra a análise para enviar.`;
  return (
    <div style={{ position: "relative", display: "flex", opacity: deleting ? 0.35 : 1, transition: "opacity 300ms ease" }}>
      <DirectoryCard type="button" $delay={delay} onClick={() => onOpen(report)} style={{ flex: 1, minWidth: 0 }}>
        <span className="head">
          <IconTile $size={40} $color={color}><FiFileText /></IconTile>
          <span className="who" style={{ paddingRight: 32 }}>
            <span className="name">{periodLabel(report)}</span>
            <span className="id" title={`${formatCount(report.companies)} empresas · criada em ${new Date(report.createdAt).toLocaleString("pt-BR")}`}>
              {formatCount(report.speds)} {report.speds === 1 ? "SPED" : "SPEDs"} · criada {new Date(report.createdAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" })}
            </span>
          </span>
        </span>
        <span className="body">
          <span className="amount">
            {formatPercent(report.pct)}
            <span style={{ fontSize: "0.5em", fontWeight: 500, opacity: 0.5, marginLeft: 6, letterSpacing: 0 }}>com XML</span>
          </span>
          <span className="meta">{formatCount(report.encontradas)} de {formatCount(report.total)} documentos</span>
          <Meter $color={color} className="bar" style={{ marginTop: 12 }}><div style={{ width: `${report.pct}%` }} /></Meter>
          <span className="taxes">
            <Tag $color={IOS.red}>{formatCount(report.faltantes)} faltantes</Tag>
            {report.nfes > 0 && <Tag>{formatCount(report.nfes)} NF-e</Tag>}
            {report.ctes > 0 && <Tag>{formatCount(report.ctes)} CT-e</Tag>}
          </span>
          {scope.expected > 0 && (
            <ScopeStrip title={scopeTitle}>
              <span className="pills" aria-hidden="true">
                {scope.cells.map((c) => <i key={`${c.cnpj}-${c.month}`} className={c.sent ? "" : "off"} />)}
              </span>
              <span className={`lbl${scope.complete ? "" : " warn"}`}>
                {scope.complete
                  ? `${formatCount(scope.sent)} de ${formatCount(scope.expected)} SPEDs`
                  : `${scope.missing === 1 ? "Falta" : "Faltam"} ${formatCount(scope.missing)} ${scope.missing === 1 ? "SPED" : "SPEDs"}`}
              </span>
            </ScopeStrip>
          )}
        </span>
      </DirectoryCard>
      <RoundButton
        type="button"
        title="Excluir análise"
        aria-label={`Excluir análise de ${periodLabel(report)}`}
        onClick={() => onDelete(report)}
        style={{ position: "absolute", top: 12, right: 12, width: 30, height: 30, fontSize: 13 }}
      >
        <FiTrash2 />
      </RoundButton>
    </div>
  );
}

// ─── Confirmação de exclusão ─────────────────────────────────────────────────

const AlertOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  font-family: ${SANS};
`;

const AlertBox = styled.div`
  width: min(340px, 100%);
  padding: 22px 20px 18px;
  border-radius: 20px;
  text-align: center;
  color: ${({ theme }) => theme.text};
  background: ${({ theme }) => (tokens.dk(theme) ? "#232327" : "#ffffff")};
  border: 1px solid ${({ theme }) => hairline(theme, true)};
  box-shadow: 0 30px 60px -16px rgba(0, 0, 0, 0.5);

  .ic {
    width: 44px; height: 44px; margin: 0 auto 12px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center; font-size: 19px;
    color: #ff453a; background: rgba(255, 69, 58, 0.12);
  }
  h4 { margin: 0; font-size: 1.02em; font-weight: 600; letter-spacing: -0.01em; }
  p { margin: 6px 0 0; font-size: 0.82em; line-height: 1.45; color: ${({ theme }) => tokens.label2(theme)}; }
  .actions { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 18px; }
  .actions button { justify-content: center; }
`;

export function ConfirmDelete({ name, onCancel, onConfirm }) {
  return createPortal(
    <AlertOverlay onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }} onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }}>
      <AlertBox role="alertdialog" aria-modal="true" aria-labelledby="del-title">
        <div className="ic"><FiTrash2 /></div>
        <h4 id="del-title">Excluir a análise de {name}?</h4>
        <p>O relatório e os arquivos gerados serão apagados. Esta ação não pode ser desfeita.</p>
        <div className="actions">
          <Capsule type="button" onClick={onCancel} autoFocus>Cancelar</Capsule>
          <TintedDanger type="button" onClick={onConfirm}><FiTrash2 /> Excluir</TintedDanger>
        </div>
      </AlertBox>
    </AlertOverlay>,
    document.body
  );
}

// ─── Tela ────────────────────────────────────────────────────────────────────

export default function RevisorHome({
  reports: rawReports, loading, processing, processingTasks = [], updatedAt,
  onRefresh, onCreate, onCreateMonth, onOpenReport, deletingId, deleteConfirm, onAskDelete, onCancelDelete, onConfirmDelete,
}) {
  const theme = useTheme();
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("period");
  const [visible, setVisible] = useState(STEP);

  const data = useMemo(() => buildRevisorHome(rawReports || []), [rawReports]);
  const initialLoading = loading && data.reports.length === 0;
  const isEmpty = !loading && data.reports.length === 0;

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    const filtered = data.reports.filter((r) => !term
      || [periodLabel(r), r.period, r.id].some((v) => String(v || "").toLowerCase().includes(term)));
    if (sort === "recent") return [...filtered].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
    if (sort === "worst") return [...filtered].sort((a, b) => a.pct - b.pct);
    return [...filtered].sort((a, b) => b.startKey.localeCompare(a.startKey));
  }, [data.reports, q, sort]);

  const progress = processingTasks.length > 0
    ? processingTasks.reduce((s, t) => s + (t.progress || 0), 0) / processingTasks.length
    : null;

  const rangeLabel = data.firstKey ? `${formatMonthKey(data.firstKey)} — ${formatMonthKey(data.lastKey)}` : "";
  const chronoPct = data.chrono.map((r) => r.pct);
  const chronoMissing = data.chrono.map((r) => r.faltantes);

  let content;
  if (initialLoading) {
    content = (
      <Section $first>
        <WidgetGrid>{[0, 1, 2, 3].map((i) => <Widget key={i}><Skeleton $h={86} $r={12} /></Widget>)}</WidgetGrid>
        <WidgetGrid style={{ marginTop: 12 }}><Widget $span={4}><Skeleton $h={230} $r={14} /></Widget></WidgetGrid>
      </Section>
    );
  } else if (isEmpty) {
    content = (
      <Section $first>
        <Group>
          <Empty>
            <div className="e-icon"><FiLayers /></div>
            <div className="e-title">Nenhuma análise de SPED ainda</div>
            <div className="e-sub" style={{ marginBottom: 16 }}>
              Envie os SPEDs de um período para conferir se cada documento escriturado tem o XML correspondente.
            </div>
            <PrimaryCapsule type="button" onClick={onCreate} disabled={processing} style={{ margin: "0 auto" }}><FiPlay /> Nova análise</PrimaryCapsule>
          </Empty>
        </Group>
      </Section>
    );
  } else {
    content = (
      <>
        {/* KPIs */}
        <Section $first>
          <WidgetGrid>
            <Widget $delay={0}>
              <div className="w-head"><FiCheckCircle style={{ color: conformityColor(data.pct), opacity: 1 }} /> Documentos com XML</div>
              <div className="w-value">{formatPercent(data.pct)}</div>
              <div className="w-foot" style={{ justifyContent: "space-between" }}>
                <span>{formatCount(data.encontradas)} de {formatCount(data.total)}</span>
                <Sparkline data={chronoPct} color={IOS.green} width={80} height={24} points={60} />
              </div>
            </Widget>
            <Widget $delay={50}>
              <div className="w-head"><FiAlertTriangle style={{ color: IOS.red, opacity: 1 }} /> XMLs faltantes</div>
              <div className="w-value">{formatCount(data.faltantes)}</div>
              <div className="w-foot" style={{ justifyContent: "space-between" }}>
                <span>{formatPercent(100 - data.pct)} dos documentos</span>
                <Sparkline data={chronoMissing} color={IOS.red} width={80} height={24} points={60} />
              </div>
            </Widget>
            <Widget $delay={100}>
              <div className="w-head"><FiFileText style={{ color: IOS.blue, opacity: 1 }} /> SPEDs processados</div>
              <div className="w-value">{formatCount(data.speds)}</div>
              <div className="w-foot">
                <span>{formatCount(data.companies.length)} {data.companies.length === 1 ? "estabelecimento" : "estabelecimentos"}</span>
              </div>
            </Widget>
            <Widget $delay={150}>
              <div className="w-head"><FiCalendar style={{ color: IOS.purple, opacity: 1 }} /> Análises</div>
              <div className="w-value">{formatCount(data.reports.length)}</div>
              <div className="w-foot">
                <span>{data.timeline.length - data.gaps.length} de {data.timeline.length} meses de {rangeLabel}</span>
              </div>
            </Widget>
          </WidgetGrid>
        </Section>

        {/* Conformidade por período + próxima análise */}
        <Section $delay={80}>
          <WidgetGrid>
            <Widget $span={3} $delay={150}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
                <div>
                  <div className="w-title">Documentos por período</div>
                  <div className="w-sub">
                    {data.worst && data.best && data.worst !== data.best
                      ? <>Menor cobertura: <b>{periodLabel(data.worst)}</b>, {formatPercent(data.worst.pct)} com XML · maior: {periodLabel(data.best)}, {formatPercent(data.best.pct)}</>
                      : "Documentos dos SPEDs com e sem XML, em cada análise"}
                  </div>
                </div>
                <div className="w-eyebrow" style={{ display: "flex", gap: 14 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: IOS.green }} />Com XML</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: IOS.red }} />Faltantes</span>
                </div>
              </div>
              <ConformityChart chrono={data.chrono} />
            </Widget>

            <Widget $delay={200} style={{ display: "flex", flexDirection: "column" }}>
              <div className="w-title">Próxima análise</div>
              <div className="w-sub">
                {data.pendingAfter.length > 0
                  ? `${data.pendingAfter.length} ${data.pendingAfter.length === 1 ? "mês fechado" : "meses fechados"} depois de ${formatMonthKey(data.lastKey)}`
                  : data.gaps.length > 0 ? `${data.gaps.length} ${data.gaps.length === 1 ? "mês sem análise" : "meses sem análise"} no intervalo` : "Todos os meses do intervalo têm análise"}
              </div>
              {data.suggestion ? (
                <>
                  <div style={{ marginTop: 18, fontSize: "1.55em", fontWeight: 700, letterSpacing: "-0.03em" }}>{formatMonthKey(data.suggestion, true)}</div>
                  <div style={{ fontSize: "0.78em", color: tokens.label2(theme), marginTop: 2 }}>
                    {data.pendingAfter.length > 0 ? "O mês seguinte ao último analisado" : "A lacuna mais recente do intervalo"}
                  </div>
                  <TintedCapsule type="button" onClick={() => onCreateMonth(data.suggestion)} disabled={processing} style={{ marginTop: 16, alignSelf: "flex-start" }}>
                    <FiPlay /> Analisar {formatMonthKey(data.suggestion)}
                  </TintedCapsule>
                </>
              ) : (
                <Capsule type="button" onClick={onCreate} disabled={processing} style={{ marginTop: 18, alignSelf: "flex-start" }}><FiPlus /> Nova análise</Capsule>
              )}
              <PeriodStats style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
                <div><span className="v">{formatCount(data.gaps.length)}</span><span className="l">lacunas no intervalo</span></div>
                <div><span className="v">{formatCount(data.pendingAfter.length)}</span><span className="l">meses após {formatMonthKey(data.lastKey)}</span></div>
              </PeriodStats>
            </Widget>
          </WidgetGrid>
        </Section>

        {/* Cobertura */}
        <Section $delay={140}>
          <WidgetGrid>
            <Widget $span={4} $delay={250}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
                <div>
                  <div className="w-title">Cobertura de SPEDs</div>
                  <div className="w-sub">Estabelecimento × mês. Abra uma análise pelo mês, ou crie uma pelas lacunas</div>
                </div>
              </div>
              <CoverageMap data={data} onOpenReport={onOpenReport} onCreateMonth={onCreateMonth} />
            </Widget>
          </WidgetGrid>
        </Section>
      </>
    );
  }

  return (
    <CreditsPage>
      <ScrolledTopBar>
        <div className="title-row">
          <div>
            <div className="eyebrow">Vidal · Revisor SPED</div>
            <h1>Revisor SPED</h1>
            <div className="meta">
              {processing ? <Pill $color={IOS.orange}>Processando SPEDs</Pill> : <LiveDot>Dados da API</LiveDot>}
              {data.reports.length > 0 && <span className="update-tag">{formatCount(data.reports.length)} {data.reports.length === 1 ? "análise" : "análises"} · {rangeLabel}</span>}
              <UpdatedAgo at={updatedAt} />
            </div>
          </div>
        </div>
        <div className="nav-row">
          <span style={{ fontSize: "0.84em", color: tokens.label2(theme) }}>
            Confere se cada documento escriturado no SPED tem o XML correspondente
          </span>
          <Toolbar>
            <RoundButton type="button" onClick={onRefresh} title="Atualizar" aria-label="Atualizar">
              <FiRefreshCw className={loading ? "spin" : ""} />
            </RoundButton>
            <ToolbarDivider />
            <PrimaryCapsule type="button" onClick={onCreate} disabled={processing}>
              {processing ? <FiRefreshCw className="spin" /> : <FiPlay />} {processing ? "Processando…" : "Nova análise"}
            </PrimaryCapsule>
          </Toolbar>
        </div>
      </ScrolledTopBar>

      {processing && (
        <Section $first>
          <Notice style={{ marginTop: 0 }}>
            <FiRefreshCw className="spin" />
            <span style={{ flex: 1 }}>
              Processando os SPEDs da nova análise{progress != null ? ` · ${Math.round(progress)}%` : ""}. A lista atualiza sozinha ao terminar.
            </span>
          </Notice>
          {progress != null && <Meter $color={T.brand} style={{ marginTop: 8 }}><div style={{ width: `${progress}%` }} /></Meter>}
        </Section>
      )}

      {content}

      {/* Diretório de análises */}
      {!initialLoading && !isEmpty && (
        <Section $delay={200}>
          <SectionHeader>
            <h2>Análises</h2>
          </SectionHeader>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <SearchField $max="320px">
              <FiSearch />
              <input placeholder="Buscar por período ou ID" aria-label="Buscar análise" value={q} onChange={(e) => { setQ(e.target.value); setVisible(STEP); }} />
              {q && <button className="clear" aria-label="Limpar busca" onClick={() => setQ("")}><FiX /></button>}
            </SearchField>
            <div style={{ flex: 1 }} />
            <SegmentedControl
              small
              value={sort}
              onChange={(v) => { setSort(v); setVisible(STEP); }}
              options={[
                { key: "period", label: "Período" },
                { key: "recent", label: "Criação" },
                { key: "worst", label: "Menor cobertura" },
              ]}
            />
          </div>

          <SupplierGrid style={{ marginTop: 14 }}>
            {!q && (
              <NewCard type="button" onClick={onCreate} disabled={processing}>
                <span className="plus"><FiPlus /></span>
                <span className="t">Nova análise</span>
                <span className="s">Envie os SPEDs de um período</span>
              </NewCard>
            )}
            {list.slice(0, visible).map((r, i) => (
              <ReportCard
                allReports={data.reports}
                key={r.id}
                report={r}
                delay={Math.min(i, 11) * 25}
                deleting={deletingId === r.id}
                onOpen={onOpenReport}
                onDelete={onAskDelete}
              />
            ))}
          </SupplierGrid>
          {list.length === 0 && (
            <Group style={{ marginTop: 10 }}>
              <Empty>
                <div className="e-icon"><FiSearch /></div>
                <div className="e-title">Nenhuma análise encontrada</div>
                <div className="e-sub">Nada encontrado para &quot;{q}&quot;.</div>
              </Empty>
            </Group>
          )}
          {visible < list.length && (
            <div style={{ marginTop: 14, textAlign: "center" }}>
              <Capsule type="button" onClick={() => setVisible((v) => v + STEP)} style={{ margin: "0 auto" }}>
                Mostrar mais <FiArrowRight />
              </Capsule>
            </div>
          )}
        </Section>
      )}

      {deleteConfirm && (
        <ConfirmDelete name={deleteConfirm.reportName} onCancel={onCancelDelete} onConfirm={onConfirmDelete} />
      )}
    </CreditsPage>
  );
}
